/**
 * @typedef {Object.<string, *>} ObjectType
 */

/**
 * Represents an object returned without dot-notation keys in typings.
 * Kept generic for editor IntelliSense compatibility with older ESLint/JSDoc parsers.
 *
 * @typedef {ObjectType} OmitDotNotationKeys
 */

/**
 * Retrieves a deeply nested property value from the provided object using dot notation.
 *
 * @template T
 * @param {T} options
 * @param {string} key
 * @returns {*}
 */
export function deepProp(options, key) {
  return String(key).split(".").reduce((acc, currentKey) => {
    if (!acc) return acc;

    return acc[currentKey];
  }, options);
}

/**
 * Creates a deep copy of the provided object using structuredClone or JSON serialization.
 *
 * @template T
 * @param {T} obj
 * @returns {T}
 */
export function deepCopy(obj) {
  try {
    const source =
      obj === undefined || obj === null
        ? /** @type {T} */ (Object.assign({}, obj))
        : obj;

    return structuredClone(source);
  } catch (_) {
    try {
      return JSON.parse(JSON.stringify(obj));
    } catch (_) {
      return obj;
    }
  }
}

/**
 * Copies only selected keys from an object.
 *
 * Keys in dot notation are resolved and assigned as their leaf property.
 *
 * @template T
 * @param {T} obj
 * @param {Array<string>} keys
 * @returns {OmitDotNotationKeys}
 */
export function partialCopy(obj, keys) {
  const copy = deepCopy(obj);
  const target = /** @type {ObjectType} */ ({});

  for (const key of Object.keys(copy)) {
    if (keys.includes(key)) {
      target[key] = copy[key];
    }
  }

  const deepKeys = keys.filter((key) => String(key).includes("."));

  if (deepKeys.length) {
    for (const key of deepKeys) {
      const deepKey = String(key).split(".").pop();

      if (!deepKey) continue;
      if (Reflect.has(target, deepKey)) continue;

      Reflect.set(target, deepKey, deepProp(copy, key));
    }
  }

  return /** @type {OmitDotNotationKeys} */ (target);
}

/**
 * Removes selected keys from an object.
 *
 * @template T
 * @param {T} obj
 * @param {Array<string>} keys
 * @returns {T}
 */
export function removeProps(obj, keys) {
  const copy = deepCopy(obj);

  for (const key of Object.keys(copy)) {
    if (keys.includes(key)) {
      delete copy[key];
    }
  }

  return copy;
}

/**
 * Shallow merges source objects into a copied base object.
 *
 * @template T
 * @param {object} source
 * @param {...object} sourceN
 * @returns {T}
 */
export function merge(source, ...sourceN) {
  const target = deepCopy(source);

  sourceN.forEach((item) => {
    Object.assign(target, item);
  });

  return /** @type {T} */ (target);
}

/**
 * Converts a JSON string to object.
 *
 * @template T
 * @param {string} value
 * @param {string} [key]
 * @returns {*}
 */
export function toObject(value, key) {
  try {
    const result = /** @type {T} */ (JSON.parse(value));

    if (key && Object.keys(result).includes(key)) {
      return result[key];
    }

    return result;
  } catch (_) {
    return {};
  }
}

/**
 * Returns a new object with keys sorted alphabetically.
 *
 * @template T
 * @param {T} obj
 * @returns {T}
 */
export function orderByKey(obj) {
  try {
    return Object.keys(obj)
      .sort()
      .reduce((target, currentKey) => {
        target[currentKey] = obj[currentKey];
        return target;
      }, /** @type {ObjectType} */ ({}));
  } catch (_) {
    return obj;
  }
}

/**
 * @typedef {"object" | "string" | "number" | "array" | "map" | "date" | "regexp"} TDuckTypes
 */

/**
 * Extracts the type of the provided value.
 *
 * @param {*} type
 * @returns {TDuckTypes}
 */
export function duckTypeExtract(type) {
  const rawType = Object.prototype.toString.call(type);

  return /** @type {TDuckTypes} */ (
    rawType.substring(7, rawType.length - 1).toLowerCase().trim()
  );
}
